import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Plus, Gift, Tag, ThumbsUp, Users, Megaphone, PhoneCall } from 'lucide-react';
import PageLayout from '../components/PageLayout';
import PricingHero from '../components/PricingHero';
import useInView from '../hooks/useInView';

const INCLUDED = [
  'Auto dialer — predictive & progressive modes',
  'CRM & lead management — CSV upload, dispositions, follow-ups',
  'Live monitoring dashboard — real-time agent and call view',
  'Campaign management — run several campaigns at once',
  'Reports & analytics — daily, weekly and per-agent breakdowns',
  'Mobile-based calling — any Android phone, any SIM',
  'Up to 10 agents, 20 campaigns and 10 concurrent calls',
  'Direct access to the WASLX founding team',
  'Your feedback shapes the product roadmap',
];

/* Icons are new, the words are not: .steprail draws a glyph inside each
   node, and a bare numeral in there reads as a bullet rather than a step. */
/* The plan a beta account actually runs on. These are the enforced
   ceilings from the platform's Basic plan, not marketing rounding — an
   account that hits one is blocked, so the page has to say so before
   sign-up rather than after. */
const BASIC_LIMITS = [
  { icon: Users,     value: '10',  label: 'users',            note: 'agents, team leads and admins together' },
  { icon: Megaphone, value: '20',  label: 'campaigns',        note: 'running at the same time' },
  { icon: PhoneCall, value: '10',  label: 'concurrent calls', note: 'across the whole organisation' },
];

const BASIC_MODULES = [
  'Dashboard and reports',
  'Customers, groups and message templates',
  'Opportunities and pipeline',
  'Campaigns and assessments',
  'Auto dial and SIP call',
  'Tasks and task statuses',
  'Teams, skills and roles',
  'Users and profiles',
  'Attendance, records and approvals',
  'Support and issue types',
  'Messages and notifications',
  'Configuration, including attendance rules',
];

const AFTER_BETA = [
  { num: '01', icon: Gift,     accent: 'accent-1', title: 'You use it free for 30 days', desc: 'Full access, whole team, zero cost.' },
  { num: '02', icon: Tag,      accent: 'accent-5', title: 'We share paid plan pricing',  desc: 'Before your beta ends we send our pricing, honestly and clearly.' },
  { num: '03', icon: ThumbsUp, accent: 'accent-4', title: 'You decide',                  desc: 'Continue on a paid plan or walk away. No auto-charge, no pressure.' },
];

const FAQS = [
  { q: 'What happens after the 1-month beta?', a: 'We reach out before the beta period ends, share our paid plan pricing, and give you the option to continue. There are no automatic charges — ever.' },
  { q: 'Do I need a credit card to join the beta?', a: 'No. Not even for verification. Sign up, get access, start calling.' },
  { q: 'How many agents can I add during beta?', a: 'Up to 10. Beta accounts run on the Basic plan, which allows 10 users, 20 campaigns and 10 concurrent calls. If your floor is larger than that, tell us before you sign up and we will raise the ceiling for your trial.' },
  { q: 'Do agents need special phones or SIM cards?', a: 'Any Android smartphone on Android 8.0 or later works. Agents use their own SIM — no hardware, no SIM boxes, no additional cost.' },
  { q: 'Can I use WASLX for inbound calls?', a: 'WASLX is currently focused on outbound calling operations. Inbound support is on the roadmap, and beta users get early access.' },
  { q: 'What support do beta users get?', a: 'Direct WhatsApp and email access to our team. No ticket queues and no bots — you talk to the people building the product.' },
];

/**
 * The price panel.
 *
 * One plan, so there is nothing to compare and no reason for a narrow
 * centred column — that shape exists to sit three tiers side by side. It is
 * split instead: what it costs and how to get it on the left, what you
 * actually get on the right, which is the only part of this page anyone
 * reads twice.
 *
 * The nine included items are on hairlines rather than in a box. A card
 * inside a card is a border for its own sake, and the panel already bounds
 * them.
 */
const PricePanel = () => {
  const [ref, inView] = useInView();

  return (
    <section ref={ref} className={`section section--surface reveal ${inView ? 'is-inview' : ''}`}>
      <div className="container-page">
        {/* The badge lives on the wrapper, not the card: the card clips its
            own overflow, which would slice a badge straddling the top edge. */}
        <div className="relative pt-3.5">
          <span className="chip absolute left-1/2 top-0 z-10 -translate-x-1/2 bg-ink text-ink-on">
            Beta — 1 month free
          </span>

          {/* Plain .card, which is --color-surface: white in light, the
              raised plane in dark. The gradient wash it used to carry put a
              second colour behind a panel whose whole job is to be read —
              the theme colour it needs is on the accent label, the ticks and
              the button, not under them. */}
          <div className="accent-brand card reveal-color !p-8 lg:!p-12">
            <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14">
              <div className="lg:border-r lg:border-line lg:pr-14">
                <div className="flex items-baseline gap-2">
                  <span className="text-hero font-bold leading-none tabular-nums text-content">₹0</span>
                  <span className="text-lede text-content-muted">/ first month</span>
                </div>
                <p className="mt-2 text-sm text-content-muted">No credit card required</p>

                <Link to="/contact" className="btn-pill btn-pill--primary btn-pill--block mt-8">
                  Claim free beta access
                  <ArrowRight className="h-[18px] w-[18px]" />
                </Link>

                <p className="mt-6 text-sm leading-relaxed text-content-muted">
                  The beta period runs 30 days from sign-up. Afterwards we share pricing and you
                  decide whether to continue — there are no automatic charges.
                </p>
              </div>

              <div>
                <p className="text-2xs font-bold uppercase tracking-[0.1em] text-accent-strong">
                  Everything included
                </p>

                <ul className="mt-5 divide-y divide-line border-t border-line">
                  {INCLUDED.map(item => (
                    <li key={item} className="flex items-start gap-3 py-3">
                      <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-pill bg-success-tint text-success-strong">
                        <Check className="h-3.5 w-3.5" />
                      </span>
                      <span className="text-[0.9375rem] leading-relaxed text-content">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * What the Basic plan is.
 *
 * The page used to say "unlimited agents", which was never true — a beta
 * account is put on the Basic plan at registration, and Basic stops at 10
 * users, 20 campaigns and 10 concurrent calls. Someone planning a 200-seat
 * floor needs that number before they sign up, not when the 11th user
 * fails to save.
 */
const BasicPlan = () => {
  const [ref, inView] = useInView();

  return (
    <section ref={ref} className={`section section--muted reveal ${inView ? 'is-inview' : ''}`}>
      <div className="container-page">
        <div className="section-head animate-fade-up">
          <span className="eyebrow accent-1">Your beta account</span>
          <h2 className="section-title">
            What the <span className="display-accent">Basic plan</span> includes
          </h2>
          <p className="section-sub">
            Beta accounts run on Basic. Every module is switched on — these are capacity ceilings,
            not feature gates. Need more room? Tell us before you sign up.
          </p>
        </div>

        <div className="mx-auto grid max-w-4xl gap-5 sm:grid-cols-3">
          {BASIC_LIMITS.map((limit, i) => {
            const Icon = limit.icon;
            return (
              <div key={limit.label} className={`accent-${i + 1} card reveal-color text-center`}>
                <span className="icon-tile mx-auto">
                  <Icon className="h-5 w-5" />
                </span>
                <div className="mt-4 text-hero font-bold leading-none tabular-nums text-content">
                  {limit.value}
                </div>
                <p className="mt-2 font-bold text-content">{limit.label}</p>
                <p className="mt-1 text-sm leading-relaxed text-content-muted">{limit.note}</p>
              </div>
            );
          })}
        </div>

        <div className="accent-brand card reveal-color mx-auto mt-6 max-w-4xl">
          <p className="text-2xs font-bold uppercase tracking-[0.1em] text-accent-strong">
            Every module, switched on
          </p>
          <ul className="mt-5 grid gap-x-8 gap-y-3 sm:grid-cols-2">
            {BASIC_MODULES.map(module => (
              <li key={module} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-pill bg-success-tint text-success-strong">
                  <Check className="h-3.5 w-3.5" />
                </span>
                <span className="text-[0.9375rem] leading-relaxed text-content">{module}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

/**
 * What happens after beta.
 *
 * Three tinted cards in a row said "pick one" about a sequence where you do
 * not pick anything — these are stages in order. It runs on .steprail now,
 * the home page's own three-step device, which draws the connector between
 * them and makes the order the point.
 */
const AfterBeta = () => {
  const [ref, inView] = useInView();

  return (
    <section ref={ref} className={`section section--muted reveal ${inView ? 'is-inview' : ''}`}>
      <div className="container-page">
        <div className="section-head animate-fade-up">
          <span className="eyebrow accent-4">After the beta</span>
          <h2 className="section-title">
            What happens <span className="display-accent">after beta?</span>
          </h2>
          <p className="section-sub">
            We will be transparent about pricing when the time comes. No surprises.
          </p>
        </div>

        <div className="steprail">
          {AFTER_BETA.map((item, i) => {
            const Icon = item.icon;
            return (
              <div
                key={item.num}
                className={`${item.accent} steprail__item reveal-color`}
                style={{ '--reveal-delay': `${i * 120}ms` }}
              >
                <span className="steprail__ghost" aria-hidden="true">{item.num}</span>
                <span className="steprail__node">
                  <Icon className="h-6 w-6" />
                </span>
                <h3 className="steprail__title">{item.title}</h3>
                <p className="steprail__body">{item.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/**
 * FAQ.
 *
 * Six shadowed boxes stacked on a plain surface read as six cards you are
 * meant to compare. They are one list, so they sit on hairlines — the same
 * treatment the included-items list and the feature benefits use.
 *
 * The toggle is a single Plus that rotates 45° into a cross, which removes
 * the Minus import and, more to the point, animates between the two states
 * instead of swapping one glyph for another.
 */
const Faq = () => {
  const [ref, inView] = useInView();
  const [open, setOpen] = useState(null);

  return (
    <section ref={ref} className={`section section--surface reveal ${inView ? 'is-inview' : ''}`}>
      <div className="container-page">
        <div className="section-head animate-fade-up">
          <span className="eyebrow accent-6">FAQ</span>
          <h2 className="section-title">
            Common <span className="display-accent">questions</span>
          </h2>
        </div>

        <div className="reveal-color mx-auto max-w-3xl divide-y divide-line border-y border-line">
          {FAQS.map((faq, i) => {
            const isOpen = open === i;
            return (
              <div key={faq.q}>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="focus-ring group flex w-full items-center justify-between gap-4 py-5 text-left"
                >
                  <span className="font-bold text-content">{faq.q}</span>
                  <span
                    className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-pill bg-surface-muted text-content-muted transition-transform duration-200 ${
                      isOpen ? 'rotate-45' : ''
                    }`}
                  >
                    <Plus className="h-4 w-4" />
                  </span>
                </button>

                <div
                  className={`grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out ${
                    isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                  }`}
                >
                  {/* min-h-0 is what makes the 0fr row actually collapse —
                      without it the child's min-content height wins and the
                      answer never fully closes. */}
                  <div className="min-h-0">
                    <p className="-mt-1 pb-5 pr-11 text-[0.9375rem] leading-relaxed text-content-muted">
                      {faq.a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Pricing = () => (
  <PageLayout
    title="Pricing — Full Platform Free for 30 Days"
    description="WASLX is in beta — the full call center platform is free for your first 30 days. No credit card, no tiers, no limits. Simple transparent pricing after beta."
    path="/pricing"
  >
    <PricingHero />
    <PricePanel />
    <BasicPlan />
    <AfterBeta />
    <Faq />
  </PageLayout>
);

export default Pricing;
